/* eslint-disable max-len */
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Icon, RotatingSpinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import useGenerationConcurrency from 'Pods/ThreeDStudio/Hooks/useGenerationConcurrency'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EmptyState from './Components/EmptyState/EmptyState'
import LoadingModelCard from './Components/LoadingModelCard/LoadingModelCard'
import ModelCard from './Components/ModelCard/ModelCard'
import NewModelCard from './Components/NewModelCard/NewModelCard'
import NotFoundState from './Components/NotFoundState/NotFoundState'
import SearchInput from './Components/SearchInput/SearchInput'
import { mockedModel } from './Constants/mock'
import useGetLibraryItems from './Hooks/useGetLibraryItems'

const size = { height: '32px', width: '32px' }

function LibraryContent({ onNavigate }) {
  const t = useTranslation()
  const pageContext = useContext(ThreeDStudioPageContext)
  const isLoggedIn = pageContext?.userLoggedIn
  const pageConfigLoaded = pageContext?.pageConfigLoaded
  const extractingCoreStyles = pageContext?.extractingCoreStyles
  const activeRuns = useThreeDStudioStore(state => state.generation.activeRuns)
  const { isPaidTier, isMultipleEnabled } = useGenerationConcurrency()
  const isGenerating = activeRuns.length > 0
  const scrollContainerRef = useRef(null)
  const sentinelRef = useRef(null)
  const [keywords, setKeywords] = useState('')
  const [searchResetKey, setSearchResetKey] = useState(0)

  const {
    items,
    hasNextPage,
    handleLoadMore,
    isFetchingNextPage,
    isLoading,
    isError,
    meta,
  } = useGetLibraryItems(isLoggedIn, keywords)

  // A variation (an enhancement, or a re-generation that joined the opened model's
  // session) renders as a "Processing..." overlay on its session's card; every other
  // in-flight run gets its own "Generating..." card. A variation whose session is not in
  // the loaded page - a deep-linked model further down the list, or an AI Assistant
  // session that holds no model yet and so is absent from the library - has no card to
  // overlay, so it falls back to a "Generating..." card rather than to no spinner at all.
  const loadedSessionIds = useMemo(
    () => new Set(items.map(({ id }) => String(id))),
    [items]
  )
  const isOverlaidVariation = useCallback(
    run => run.enhancingSessionId != null && loadedSessionIds.has(String(run.enhancingSessionId)),
    [loadedSessionIds]
  )
  const loadingRuns = useMemo(
    () => activeRuns.filter(run => !isOverlaidVariation(run)),
    [activeRuns, isOverlaidVariation]
  )
  const processingSessionIds = useMemo(
    () => new Set(
      activeRuns
        .filter(isOverlaidVariation)
        .map(run => String(run.enhancingSessionId))
    ),
    [activeRuns, isOverlaidVariation]
  )
  // Paid tiers keep the "Generate New 3D Model" card while generating - at the cap too,
  // where it still opens the starting page and only the Generate button is blocked. Free
  // tiers - and everyone while the :enable_multiple_generations flag is off - keep the
  // legacy behavior of swapping it for the single "Generating..." card.
  const showNewModelCard = (isMultipleEnabled && isPaidTier) || loadingRuns.length === 0

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    const sentinel = sentinelRef.current
    if (!scrollContainer || !sentinel || !hasNextPage || isFetchingNextPage) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          handleLoadMore()
        }
      },
      { root: scrollContainer, rootMargin: '100px', threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, handleLoadMore])

  const remainingCount = Math.max(0, (meta?.totalCount ?? 0) - items.length)
  const loadingMoreSkeletonCount = Math.min(
    remainingCount,
    Number(meta?.perPage) || 10
  )
  const shouldShowSkeletonItems = !extractingCoreStyles
    && items.length > 0
    && hasNextPage
    && isFetchingNextPage
    && loadingMoreSkeletonCount > 0
  const authPending = !extractingCoreStyles && !pageConfigLoaded
  const shouldShowLoading = !extractingCoreStyles && !isError && (authPending || (isLoggedIn && isLoading))
  const shouldShowError = isError && !isLoading
  const shouldShowContent = extractingCoreStyles || (!authPending && !isLoading && (items.length > 0 || isGenerating))
  const shouldShowPlaceholder = !extractingCoreStyles
    && !authPending
    && !isLoading
    && !isError
    && !isGenerating
    && items.length === 0
  const hasSearched = keywords.length > 0
  const shouldShowEmptyState = shouldShowPlaceholder && !hasSearched
  const shouldShowNotFound = shouldShowPlaceholder && hasSearched
  const shouldShowSearch = extractingCoreStyles || (meta?.totalCount ?? 0) >= 10 || keywords.length > 0

  const handleClearSearch = useCallback(() => {
    setKeywords('')
    // Remount SearchInput to reset its internal input value (and pending debounce).
    setSearchResetKey(key => key + 1)
  }, [])

  return (
    <div className="library-content">
      {shouldShowSearch && <SearchInput key={searchResetKey} onSearch={setKeywords} />}
      {shouldShowLoading && (
        <div className="library-content__loading">
          <Icon svgComponent={RotatingSpinner} size={size} />
        </div>
      )}
      {shouldShowError && (
        <div className="library-content__error">{t('sidebar.library.error')}</div>
      )}
      {shouldShowEmptyState && <EmptyState />}
      {shouldShowNotFound && <NotFoundState onNavigate={onNavigate} onClearSearch={handleClearSearch} />}
      {shouldShowContent && (
        <div
          ref={scrollContainerRef}
          className="model-card-list"
        >
          {showNewModelCard && <NewModelCard onNavigate={onNavigate} />}
          {loadingRuns.map(run => (
            <LoadingModelCard
              key={run.clientId ?? `run-${run.runId}`}
              run={run}
              isActiveOnAiPage={!showNewModelCard}
              onNavigate={onNavigate}
              content={t('sidebar.status.generating')}
            />
          ))}
          {!extractingCoreStyles && items.map(({ id, ...rest }) => (
            <ModelCard
              key={`model-card-${id}`}
              sessionId={id}
              isProcessing={processingSessionIds.has(String(id))}
              onNavigate={onNavigate}
              {...rest}
            />
          ))}
          {extractingCoreStyles && (
            <>
              <ModelCard sessionId={mockedModel.id} attributes={mockedModel.attributes} onNavigate={onNavigate} />
              <ModelCard
                sessionId={mockedModel.id}
                attributes={mockedModel.attributes}
                isProcessing
                onNavigate={onNavigate}
              />
              <LoadingModelCard onNavigate={onNavigate} content={t('sidebar.status.generating')} />
              <div className="library-content__loading-more">
                <LoadingModelCard />
              </div>
            </>
          )}
          {shouldShowSkeletonItems && (
            <div className="library-content__loading-more">
              {Array.from({ length: loadingMoreSkeletonCount }, (_, i) => (
                <LoadingModelCard key={`loading-more-${i}`} />
              ))}
            </div>
          )}
          {items.length > 0 && hasNextPage && (
            <div
              ref={sentinelRef}
              className="library-content__sentinel"
              aria-hidden="true"
            />
          )}
        </div>
      )}
    </div>
  )
}

LibraryContent.propTypes = {
  onNavigate: func.isRequired,
}

export default memo(LibraryContent)
